<template>
    <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div class="px-4 py-4 sm:p-5">
            <div class="flex items-center justify-between mb-3">
                <h3
                    class="text-base leading-6 font-medium text-gray-900 dark:text-white"
                >
                    {{ displayTitle }}
                </h3>
                <router-link
                    v-if="showViewAll && viewAllRoute"
                    :to="viewAllRoute"
                    class="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                >
                    {{ t("dashboard.materials.viewAll") }}
                </router-link>
            </div>

            <!-- Empty State -->
            <div
                v-if="!materials || materials.length === 0"
                class="text-center py-8"
            >
                <FileText class="mx-auto h-12 w-12 text-gray-400" />
                <h3
                    class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                    {{ displayEmptyStateTitle }}
                </h3>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {{ displayEmptyStateMessage }}
                </p>
            </div>

            <!-- Materials List -->
            <div v-else class="space-y-2">
                <div
                    v-for="material in materials"
                    :key="material.id"
                    class="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-150"
                >
                    <div class="flex items-center flex-1 min-w-0">
                        <!-- Type Icon -->
                        <div class="flex-shrink-0">
                            <div
                                :class="[
                                    'w-8 h-8 rounded-full flex items-center justify-center',
                                    getMaterialTypeIconBg(material.type),
                                ]"
                            >
                                <component
                                    :is="getMaterialTypeIcon(material.type)"
                                    :class="[
                                        'w-4 h-4',
                                        getMaterialTypeIconColor(material.type),
                                    ]"
                                />
                            </div>
                        </div>

                        <!-- Material Info -->
                        <div class="ml-3 flex-1 min-w-0">
                            <p
                                class="text-sm font-medium text-gray-900 dark:text-white truncate"
                            >
                                {{ material.title }}
                            </p>
                            <div
                                class="flex items-center space-x-2 mt-1 text-sm text-gray-500 dark:text-gray-400"
                            >
                                <span class="truncate">{{
                                    material.classSubject
                                }}</span>
                                <span
                                    v-if="material.subject"
                                    class="hidden sm:inline"
                                    >•</span
                                >
                                <span
                                    v-if="material.subject"
                                    class="hidden sm:inline truncate"
                                    >{{ material.subject }}</span
                                >
                                <span
                                    v-if="showTeacher && material.teacher"
                                    class="hidden md:inline"
                                    >•</span
                                >
                                <span
                                    v-if="showTeacher && material.teacher"
                                    class="hidden md:inline truncate"
                                    >{{ material.teacher }}</span
                                >
                            </div>
                            <div
                                class="flex items-center space-x-2 mt-1 text-xs text-gray-500 dark:text-gray-400"
                            >
                                <span>{{
                                    formatDate(material.createdAt)
                                }}</span>
                                <span v-if="material.fileSize">•</span>
                                <span v-if="material.fileSize">{{
                                    material.fileSize
                                }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Status and Actions -->
                    <div class="ml-3 flex-shrink-0">
                        <div class="flex items-center space-x-2">
                            <!-- Status Badge -->
                            <span
                                v-if="showStatus"
                                :class="[
                                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                                    getStatusClasses(material.status),
                                ]"
                            >
                                {{ getStatusText(material.status) }}
                            </span>

                            <!-- Edit Button (for teachers) -->
                            <button
                                v-if="showEdit"
                                @click="editMaterial(material)"
                                class="p-1.5 text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors duration-150"
                                :title="t('common.edit')"
                            >
                                <Pencil class="w-4 h-4" />
                            </button>

                            <!-- Open Button (for URL type) -->
                            <button
                                v-if="material.type === 'url'"
                                @click="openMaterial(material)"
                                class="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-150"
                                :title="t('common.open')"
                            >
                                <Link class="w-4 h-4" />
                            </button>

                            <!-- Download Button (for file type) -->
                            <button
                                v-if="material.type === 'file'"
                                @click="downloadMaterial(material)"
                                class="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-150"
                                :title="t('common.download')"
                            >
                                <File class="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { MaterialItem } from "@/types/dashboard";
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import { FileText, File, Link, Pencil } from "@lucide/vue";

const { t } = useI18n();

interface Props {
    title: string;
    materials: MaterialItem[];
    showViewAll?: boolean;
    viewAllRoute?: string;
    showStatus?: boolean;
    showEdit?: boolean;
    showTeacher?: boolean;
    emptyStateTitle?: string;
    emptyStateMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
    showViewAll: false,
    showStatus: true,
    showEdit: false,
    showTeacher: false,
    emptyStateTitle: "",
    emptyStateMessage: "",
});

// Computed properties for translations with fallback to props
const displayTitle = computed(() => {
    return props.title || t("dashboard.recentMaterials");
});

const displayEmptyStateTitle = computed(() => {
    return props.emptyStateTitle || t("dashboard.materials.emptyTitle");
});

const displayEmptyStateMessage = computed(() => {
    return props.emptyStateMessage || t("dashboard.materials.emptyMessage");
});

const emit = defineEmits<{
    view: [material: MaterialItem];
    edit: [material: MaterialItem];
    download: [material: MaterialItem];
    open: [material: MaterialItem];
}>();

const getMaterialTypeIcon = (type: string) => {
    return type === "url" ? Link : File;
};

const getMaterialTypeIconBg = (type: string) => {
    return type === "url"
        ? "bg-purple-100 dark:bg-purple-900"
        : "bg-yellow-100 dark:bg-yellow-900";
};

const getMaterialTypeIconColor = (type: string) => {
    return type === "url"
        ? "text-purple-600 dark:text-purple-300"
        : "text-yellow-600 dark:text-yellow-300";
};

const getStatusClasses = (status: string) => {
    const classes = {
        published:
            "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300",
        draft: "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300",
    };
    return classes[status as keyof typeof classes] || classes.draft;
};

const getStatusText = (status: string) => {
    return status === "published"
        ? t("dashboard.materials.status.published")
        : t("dashboard.materials.status.draft");
};

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
        return "Today";
    } else if (diffDays === 1) {
        return "Yesterday";
    } else if (diffDays < 7) {
        return `${diffDays} days ago`;
    } else {
        return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    }
};

const viewMaterial = (material: MaterialItem) => {
    // Emit view event with material data
    emit("view", material);
};

const editMaterial = (material: MaterialItem) => {
    // Emit edit event with material data
    emit("edit", material);
};

const openMaterial = (material: MaterialItem) => {
    // Emit open event with material data
    emit("open", material);
};

const downloadMaterial = (material: MaterialItem) => {
    // Emit download event with material data
    emit("download", material);
};
</script>
